import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { absoluteUrl, SEO_LOCALE_KEYS, SUPPORTED_SEO_LOCALES, SeoLocaleKey } from './constants';

interface PageSeoProps {
  path: string;
  titleKey: string;
  descriptionKey: string;
  ogImage?: string;
  keywords?: string[];
  noindex?: boolean;
  jsonLd?: Array<Record<string, any>>;
  /*
   * Open Graph type. 'website' suits the marketing pages; an article should say so,
   * which lets a share card show a byline and a date instead of a generic site card.
   * The BlogPosting JSON-LD already carries both, so only the OG side was missing.
   */
  ogType?: 'website' | 'article';
  /** ISO date, emitted as article:published_time. Only meaningful with ogType 'article'. */
  publishedTime?: string;
  /** Emitted as article:author. Only meaningful with ogType 'article'. */
  author?: string;
}

const DEFAULT_OG_IMAGE = '/og/og-default.png';

function localeKeyFromPath(pathname: string): SeoLocaleKey {
  const m = pathname.match(/^\/(es|de|fr|ja|pt-BR|zh-CN|ko)(\/|$)/);
  return m ? (m[1] as SeoLocaleKey) : 'en';
}

export const PageSeo: React.FC<PageSeoProps> = ({
  path, titleKey, descriptionKey, ogImage, keywords, noindex, jsonLd,
  ogType = 'website', publishedTime, author,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const localeKey = localeKeyFromPath(location.pathname);
  const ogLocale = SUPPORTED_SEO_LOCALES[localeKey].ogLocale;
  const fullTitle = `${t(titleKey)} — Localingos`;
  const description = t(descriptionKey);
  const canonical = absoluteUrl(path);
  const pageUrl = absoluteUrl(path, localeKey);
  const imageUrl = absoluteUrl(ogImage || DEFAULT_OG_IMAGE);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <link rel="canonical" href={canonical} />

      {SEO_LOCALE_KEYS.map(k => (
        <link key={k} rel="alternate" hrefLang={SUPPORTED_SEO_LOCALES[k].hreflang} href={absoluteUrl(path, k)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      <meta property="og:type" content={ogType} />
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && author && <meta property="article:author" content={author} />}
      <meta property="og:site_name" content="Localingos" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLd?.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
};
